/**
 * 实体表（campaign / adset / ad）的列定义。
 *
 * 数据源：`GET /v1/metrics/entities`（台账 + 两个口径的指标），因此表里会出现
 * **花费 / metaROAS / UTM ROAS** 三列。三个数字的口径必须分列：
 *   - `spendCents`：Meta 平台自报花费；
 *   - `metaRoas`（口径 1）：平台自报转化价值 ÷ 花费；
 *   - `utmRoas`（口径 3，参考）：我们按 UTM 匹到的订单额 ÷ 花费。它只覆盖约 15% 的订单，
 *     列头会同时标注整页覆盖率，绝不能当成全量口径。
 *
 * `null` 一律渲染「—」（不是 0）：分母 0 的比率是「无法计算」，与「真实为零」相反。
 * 类型上指标字段是**可选**的（`EntityTableRow`），这样选择器/排序的单测仍可用纯台账行。
 *
 * 金额口径：`dailyBudgetCents` / `lifetimeBudgetCents` / `spendCents` 都是**账户币种最小单位整数**。
 */

import type { AdEntityLevel, AdEntityRow, EntityMetricsFields } from "../lib/apiTypes.js";
import { DASH, formatCentsOrDash, formatRoas } from "../lib/format.js";

/** 表格行：台账字段 +（可能缺失的）指标字段。 */
export type EntityTableRow = AdEntityRow & Partial<EntityMetricsFields>;

export interface EntityColumnSpec {
  readonly id: string;
  readonly label: string;
  readonly hint: string;
  readonly width: number;
  readonly align: "start" | "end";
  readonly text: (row: EntityTableRow) => string;
  readonly sortValue: (row: EntityTableRow) => number | string | null;
}

export const ENTITY_LEVEL_LABEL: Readonly<Record<AdEntityLevel, string>> = {
  campaign: "广告系列",
  adset: "广告组",
  ad: "广告",
};

/** 状态展示：Meta 的状态值是英文大写；未知值原样显示，不猜。 */
export function entityStatusText(status: string): string {
  switch (status.toUpperCase()) {
    case "ACTIVE":
      return "投放中";
    case "PAUSED":
      return "已暂停";
    case "ARCHIVED":
      return "已归档";
    case "DELETED":
      return "已删除";
    case "IN_PROCESS":
      return "处理中";
    case "WITH_ISSUES":
      return "有异常";
    default:
      return status === "" ? DASH : status;
  }
}

export const entityColumns: readonly EntityColumnSpec[] = [
  {
    id: "level",
    label: "层级",
    hint: "Meta 实体层级：campaign / adset / ad",
    width: 84,
    align: "start",
    text: (row) => ENTITY_LEVEL_LABEL[row.level],
    sortValue: (row) => row.level,
  },
  {
    id: "name",
    label: "名称",
    hint: "实体名称（来自 /v1/entities）",
    width: 280,
    align: "start",
    text: (row) => row.name,
    sortValue: (row) => row.name,
  },
  {
    id: "status",
    label: "状态",
    hint: "Meta 的 status 字段",
    width: 96,
    align: "start",
    text: (row) => entityStatusText(row.status),
    sortValue: (row) => row.status,
  },
  {
    id: "effectiveStatus",
    label: "生效状态",
    hint: "effective_status：受父级暂停影响后的实际状态",
    width: 104,
    align: "start",
    text: (row) => entityStatusText(row.effectiveStatus),
    sortValue: (row) => row.effectiveStatus,
  },
  {
    id: "dailyBudgetCents",
    label: "日预算",
    hint: "daily_budget（账户币种最小单位整数）；null = 未设置/未知，渲染「—」",
    width: 112,
    align: "end",
    text: (row) => formatCentsOrDash(row.dailyBudgetCents, row.currency),
    sortValue: (row) => row.dailyBudgetCents,
  },
  {
    id: "lifetimeBudgetCents",
    label: "总预算",
    hint: "lifetime_budget（账户币种最小单位整数）；null = 未设置/未知",
    width: 112,
    align: "end",
    text: (row) => formatCentsOrDash(row.lifetimeBudgetCents, row.currency),
    sortValue: (row) => row.lifetimeBudgetCents,
  },
  {
    id: "spendCents",
    label: "花费",
    hint: "Meta 平台自报花费（账户币种最小单位整数）；0 = 窗口内确实没有花费，缺指标时显示「—」",
    width: 120,
    align: "end",
    text: (row) => formatCentsOrDash(row.spendCents ?? null, row.currency),
    sortValue: (row) => row.spendCents ?? null,
  },
  {
    id: "metaRoas",
    label: "metaROAS",
    hint: "口径 1：平台自报转化价值 ÷ 花费；分母 0 渲染「—」。它不是店铺真实 ROAS（那是 /v1/reconcile 的 reconcileRoas）",
    width: 96,
    align: "end",
    text: (row) => formatRoas(row.metaRoas ?? null),
    sortValue: (row) => row.metaRoas ?? null,
  },
  {
    id: "accountId",
    label: "账户",
    hint: "Meta 广告账户 id",
    width: 120,
    align: "start",
    text: (row) => row.accountId,
    sortValue: (row) => row.accountId,
  },
  {
    id: "updatedAtIso",
    label: "更新时间",
    hint: "实体在 Meta 侧的最后更新时间（ISO-8601）",
    width: 180,
    align: "start",
    text: (row) => row.updatedAtIso,
    sortValue: (row) => row.updatedAtIso,
  },
];

export const ENTITY_COLUMN_BY_ID: ReadonlyMap<string, EntityColumnSpec> = new Map(
  entityColumns.map((column) => [column.id, column]),
);

export const ENTITY_SELECT_WIDTH = 36;
/** 行内快捷操作列（暂停/启动 + 改预算）。 */
export const ENTITY_ACTION_WIDTH = 168;

export const ENTITY_TABLE_WIDTH =
  ENTITY_SELECT_WIDTH + entityColumns.reduce((total, column) => total + column.width, 0) + ENTITY_ACTION_WIDTH;

export const ENTITY_GRID_TEMPLATE = `${ENTITY_SELECT_WIDTH}px ${entityColumns
  .map((column) => `${column.width}px`)
  .join(" ")} ${ENTITY_ACTION_WIDTH}px`;

/** 该实体是否支持预算调整（广告层没有预算字段）。 */
export function supportsBudget(level: AdEntityLevel): boolean {
  return level === "campaign" || level === "adset";
}
